/**
 * Controlled / narcotic drug helpers for pharmacy dispensing.
 */

function ss(key) {
  const v = sessionStorage.getItem(key);
  return v && v !== 'null' && v !== 'undefined' ? v : null;
}

export function isControlledOrNarcotic(item) {
  if (!item || typeof item !== 'object') return false;
  return Boolean(
    item.is_controlled_drug
    || item.is_narcotic
    || item.controlled,
  );
}

export function buildPharmacistAuthorization(pharmacistId, licenseNumber = '') {
  const authorizedBy = pharmacistId || ss('USER_ACCESS_KEY');
  const payload = {
    authorized_by: authorizedBy,
    authorized_at: new Date().toISOString(),
  };
  if (licenseNumber && String(licenseNumber).trim()) {
    payload.license_number = String(licenseNumber).trim();
  }
  return payload;
}

/** UI status — backend sends PENDING, legacy mocks use AWAITING_AUTHORIZATION. */
export function normalizeControlledAuthStatus(status) {
  const s = String(status || '').toUpperCase();
  if (s === 'PENDING') return 'AWAITING_AUTHORIZATION';
  if (s === 'DENIED') return 'REJECTED';
  return s || 'AWAITING_AUTHORIZATION';
}

export function isControlledAuthPending(status) {
  const normalized = normalizeControlledAuthStatus(status);
  return normalized === 'AWAITING_AUTHORIZATION';
}

export function isControlledAuthApproved(status) {
  return String(status || '').toUpperCase() === 'AUTHORIZED';
}

export function isControlledAuthRejectedStatus(status) {
  const s = String(status || '').toUpperCase();
  return s === 'DENIED' || s === 'REJECTED';
}

function isPharmacistRoleName(name) {
  return String(name || '').toLowerCase().replace(/[_\s]+/g, ' ').trim() === 'pharmacist';
}

function collectRoleNames(userInfo) {
  if (!userInfo || typeof userInfo !== 'object') return [];
  const names = [];
  if (userInfo.role) names.push(userInfo.role);
  const entries = Array.isArray(userInfo.role_permission) ? userInfo.role_permission : [];
  entries.forEach((entry) => {
    if (entry?.role) names.push(entry.role);
  });
  return names;
}

/** True when get_logged_in_user_info (or login) carries role "pharmacist". */
export function userHasPharmacistRole(userInfo) {
  return collectRoleNames(userInfo).some(isPharmacistRoleName);
}

/** Pharmacist role from user info, falling back to sessionStorage.roleType. */
export function isLoggedInPharmacist(userInfo) {
  if (userInfo && typeof userInfo === 'object') {
    const hasRoleField = Boolean(userInfo.role);
    const hasRoleList = Array.isArray(userInfo.role_permission);
    if (hasRoleField || hasRoleList) {
      return userHasPharmacistRole(userInfo);
    }
  }
  try {
    return isPharmacistRoleName(sessionStorage.getItem('roleType'));
  } catch {
    return false;
  }
}

/**
 * Merge a controlled-queue row into the Direct Dispense line's controlledAuth.
 * Returns the current object when nothing changed (so React can skip a render).
 */
export function applyControlledAuthQueueUpdate(currentAuth, queueRow) {
  if (!currentAuth?.authId || !queueRow || typeof queueRow !== 'object') {
    return currentAuth || null;
  }
  const rowId = queueRow.id || queueRow.auth_id || queueRow.queue_id;
  if (rowId == null || String(rowId) !== String(currentAuth.authId)) {
    return currentAuth;
  }

  const raw = queueRow._raw && typeof queueRow._raw === 'object' ? queueRow._raw : queueRow;
  const rawStatus = raw.status || queueRow.status;
  const approved = isControlledAuthApproved(rawStatus);
  const rejected = isControlledAuthRejectedStatus(rawStatus);

  let next;
  if (approved) {
    next = {
      ...currentAuth,
      status: 'AUTHORIZED',
      approvedBy: raw.supervisor_id || raw.authorized_by || raw.approved_by || currentAuth.approvedBy,
      approvedAt: raw.authorized_at || raw.approved_at || currentAuth.approvedAt,
      rejected: false,
    };
  } else if (rejected) {
    next = {
      ...currentAuth,
      status: 'DENIED',
      rejected: true,
      rejectedBy: raw.supervisor_id || raw.denied_by || raw.rejected_by || currentAuth.rejectedBy,
      rejectedAt: raw.denied_at || raw.rejected_at || currentAuth.rejectedAt,
    };
  } else {
    next = {
      ...currentAuth,
      status: rawStatus || currentAuth.status || 'PENDING',
    };
  }

  if (
    next.status === currentAuth.status
    && next.approvedBy === currentAuth.approvedBy
    && next.rejectedBy === currentAuth.rejectedBy
    && Boolean(next.rejected) === Boolean(currentAuth.rejected)
  ) {
    return currentAuth;
  }
  return next;
}

export function isControlledLineNonReturnable(itemId, catalog) {
  if (!itemId || !catalog) return false;
  const item = catalog.resolveById?.(itemId) || catalog.byId?.get?.(itemId);
  if (item) return isControlledOrNarcotic(item);
  return catalog.isControlled?.(itemId) ?? false;
}

/** Parent-sale statuses that mean an order needs no further counter action. */
const CLOSED_ORDER_STATUSES = new Set(['FINALIZED', 'CANCELLED']);

export function isClosedControlledOrder(sourceStatus) {
  return CLOSED_ORDER_STATUSES.has(String(sourceStatus || '').toUpperCase());
}

/**
 * One status for a whole order, from its individual line authorizations.
 * Worst-first: a single denial blocks the order, and one line still pending
 * keeps the whole thing waiting — payment is all-or-nothing.
 */
export function rollupControlledStatus(lines) {
  if (!lines?.length) return 'AWAITING_AUTHORIZATION';
  if (lines.some((l) => normalizeControlledAuthStatus(l.status) === 'REJECTED')) return 'REJECTED';
  if (lines.some((l) => isControlledAuthPending(l.status))) return 'AWAITING_AUTHORIZATION';
  return 'AUTHORIZED';
}

/**
 * Collapse controlled-authorization rows into the orders they belong to.
 * The queue API returns one row per line; the counter thinks in orders.
 * Closed orders are dropped — they are nothing to come back to.
 */
export function groupControlledOrders(rows) {
  const byOrder = new Map();
  (rows || []).forEach((row) => {
    const orderId = row.transaction_id;
    if (!orderId || isClosedControlledOrder(row.source_status)) return;
    const existing = byOrder.get(orderId);
    if (existing) {
      existing.lines.push(row);
      return;
    }
    byOrder.set(orderId, {
      orderId,
      orderNumber: row.source_number,
      sourceType: row.source_type,
      patientName: row.patient_name,
      patientMobile: row.patient_mobile,
      resumePath: row.resume_path,
      requestedAt: row.requested_at,
      lines: [row],
    });
  });
  return [...byOrder.values()].map((order) => ({
    ...order,
    status: rollupControlledStatus(order.lines),
  }));
}
